import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useBillArchives, useToggleLegalHold } from "@/hooks/useArchive";
import { Archive, Calendar, Shield, Download, Lock, Unlock, AlertTriangle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";

export function ArchiveManagementDashboard({ workspaceId }: { workspaceId: string | undefined }) {
  const { data: archives, isLoading, refetch } = useBillArchives(workspaceId || '');
  const toggleLegalHold = useToggleLegalHold();
  const { toast } = useToast();
  
  const [legalHoldDialog, setLegalHoldDialog] = useState<{ open: boolean; archive: any | null; enabling: boolean }>({
    open: false,
    archive: null,
    enabling: true
  });
  const [legalHoldReason, setLegalHoldReason] = useState('');

  const handleToggleLegalHold = async () => {
    if (!legalHoldDialog.archive) return;
    
    try {
      await toggleLegalHold.mutateAsync({
        archiveId: legalHoldDialog.archive.id,
        legalHold: legalHoldDialog.enabling,
        reason: legalHoldReason
      });
      
      toast({
        title: legalHoldDialog.enabling ? "Legal Hold Enabled" : "Legal Hold Removed",
        description: legalHoldDialog.enabling 
          ? "This bill cannot be deleted until legal hold is removed."
          : "Legal hold has been removed from this bill."
      });
      
      setLegalHoldDialog({ open: false, archive: null, enabling: true });
      setLegalHoldReason('');
      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update legal hold status",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return <div>Loading archives...</div>;
  }

  const legalHoldCount = archives?.filter(a => a.legal_hold).length || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Archived Bills</h3>
        <div className="flex items-center gap-2">
          {legalHoldCount > 0 && (
            <Badge variant="outline" className="bg-red-50 dark:bg-red-950/20 text-red-600">
              <Shield className="h-3 w-3 mr-1" />
              {legalHoldCount} Legal Hold
            </Badge>
          )}
          <Badge variant="outline">{archives?.length || 0} archived</Badge>
        </div>
      </div>

      <div className="space-y-3">
        {archives?.map((archive) => (
          <Card key={archive.id} className={`hover:shadow-md transition-shadow ${archive.legal_hold ? 'border-red-300 dark:border-red-800' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-3">
                    <Archive className="h-5 w-5 text-muted-foreground" />
                    <p className="font-medium">Bill ID: {archive.bill_id?.substring(0, 8)}...</p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {archive.legal_hold && (
                      <Badge className="bg-red-500/10 text-red-500 border-red-500">
                        <Lock className="h-3 w-3 mr-1" />
                        Legal Hold Active
                      </Badge>
                    )}
                    {archive.archived_at && (
                      <Badge variant="outline">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDistanceToNow(new Date(archive.archived_at), { addSuffix: true })}
                      </Badge>
                    )}
                    {archive.retention_expires_at && (
                      <Badge variant="outline">
                        Expires: {new Date(archive.retention_expires_at).toLocaleDateString()}
                      </Badge>
                    )}
                  </div>

                  {archive.archive_reason && (
                    <p className="text-sm text-muted-foreground">
                      Reason: {archive.archive_reason}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  {/* Legal Hold Toggle */}
                  <div className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30">
                    <Label htmlFor={`legal-hold-${archive.id}`} className="text-xs cursor-pointer">
                      {archive.legal_hold ? (
                        <Lock className="h-4 w-4 text-red-500" />
                      ) : (
                        <Unlock className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Label>
                    <Switch
                      id={`legal-hold-${archive.id}`}
                      checked={archive.legal_hold || false}
                      onCheckedChange={(checked) => {
                        setLegalHoldDialog({
                          open: true,
                          archive,
                          enabling: checked
                        });
                      }}
                    />
                    <span className="text-xs text-muted-foreground">Legal Hold</span>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                    <Button 
                      size="sm" 
                      disabled={archive.legal_hold}
                      title={archive.legal_hold ? "Cannot restore while under legal hold" : "Request restore"}
                    >
                      Request Restore
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {(!archives || archives.length === 0) && (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              No archived bills
            </CardContent>
          </Card>
        )}
      </div>

      {/* Legal Hold Confirmation Dialog */}
      <Dialog open={legalHoldDialog.open} onOpenChange={(open) => setLegalHoldDialog(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {legalHoldDialog.enabling ? (
                <>
                  <Lock className="h-5 w-5 text-red-500" />
                  Enable Legal Hold
                </>
              ) : (
                <>
                  <Unlock className="h-5 w-5 text-green-500" />
                  Remove Legal Hold
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {legalHoldDialog.enabling ? (
                <>
                  <AlertTriangle className="h-4 w-4 inline mr-1 text-yellow-500" />
                  Enabling legal hold will prevent this bill from being deleted or modified.
                  This is typically used for bills under audit or legal review.
                </>
              ) : (
                "Removing legal hold will allow normal operations on this bill including deletion."
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="legal-hold-reason">
                Reason {legalHoldDialog.enabling && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                id="legal-hold-reason"
                placeholder={legalHoldDialog.enabling 
                  ? "Enter the reason for legal hold (e.g., 'Under IRS audit', 'Legal dispute')"
                  : "Optional: Enter reason for removing legal hold"
                }
                value={legalHoldReason}
                onChange={(e) => setLegalHoldReason(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setLegalHoldDialog({ open: false, archive: null, enabling: true })}>
              Cancel
            </Button>
            <Button 
              onClick={handleToggleLegalHold}
              disabled={legalHoldDialog.enabling && !legalHoldReason.trim()}
              variant={legalHoldDialog.enabling ? "destructive" : "default"}
            >
              {legalHoldDialog.enabling ? "Enable Legal Hold" : "Remove Legal Hold"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
